// Scent Society client-side gate.
//
// Why: storefront is on Shopify New Customer Accounts, so `{{ customer.tags }}`
// in Liquid is unreliable for gating. We render gated PDPs locked by default
// and ask the portal — which IS the source of truth for membership — whether
// to unlock.
//
// Contract: GET https://account.elementbrooklyn.com/api/me with credentials
// must return `{ logged_in: boolean, scent_society: boolean }`. CORS must
// allow https://www.elementbrooklyn.com with credentials.
//
// Server-side enforcement (Shopify Cart Validation Function reading
// product tags) is required for security — this script is UI only.

(function () {
  var PORTAL_API = 'https://account.elementbrooklyn.com/api/me';
  var CACHE_KEY = 'eb_scent_society_state';
  var CACHE_TTL_MS = 5 * 60 * 1000;
  var BODY_CLASS = 'scent-society-member';

  function setMembership(isMember) {
    document.body.classList.toggle(BODY_CLASS, !!isMember);
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() > parsed.expires_at) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(isMember) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        scent_society: !!isMember,
        expires_at: Date.now() + CACHE_TTL_MS,
      }));
    } catch (e) { /* private mode etc. — skip */ }
  }

  // Shared membership signal. assets/sale-event.js consumes this (plus the
  // `eb:membership` CustomEvent below) instead of calling /api/me itself, so
  // a US page makes exactly one portal request. `pending` flips to false
  // once the portal has answered (or failed); `member` is the best-known
  // value at any moment (cache first, then the live answer).
  var cached = readCache();
  window.EBMembership = window.EBMembership || {
    pending: true,
    member: cached ? !!cached.scent_society : null,
  };

  // Signed into the portal but not the SHOP. The portal decides membership
  // (so the page unlocks), but Shopify's cart-validation Function reads the
  // cart's own customer, which only exists once the shop itself is signed in.
  // Signing in via the header Account icon, or arriving from an ad or email,
  // leaves the shop signed out, so a real member's add-to-cart is refused with
  // the Function's "exclusive to Scent Society members" message.
  //
  // Add-to-cart callers hand that message here. For a known member we send the
  // shopper through the shop's own sign-in and back to this page. That's silent
  // while their Shopify login is warm (it just was, for the portal) and asks for
  // a code once otherwise. At most once per 5 minutes per tab, so a sign-in that
  // doesn't take shows the normal error instead of looping. Returns true when it
  // navigated, so the caller skips its own error UI. (Once the Function enforces
  // at checkout only, adds stop being refused; the checkout-click hop below
  // covers that case, and this stays for any cart path that still 422s.)
  var SHOP_SIGN_IN_KEY = 'eb_ss_shop_signin_at';
  var MEMBERS_ONLY_RE = /exclusive to Scent Society members/i;
  window.EBMembership.recoverShopSignIn = function (errorMessage) {
    if (!MEMBERS_ONLY_RE.test(String(errorMessage || ''))) return false;
    if (window.EBMembership.member !== true) return false;
    try {
      var last = parseInt(sessionStorage.getItem(SHOP_SIGN_IN_KEY) || '0', 10);
      if (Date.now() - last < 5 * 60 * 1000) return false;
      sessionStorage.setItem(SHOP_SIGN_IN_KEY, String(Date.now()));
    } catch (e) {
      return false; // no storage means no loop guard, so don't redirect at all
    }
    var returnTo = window.location.pathname + window.location.search;
    window.location.assign('/customer_authentication/login?return_to=' + encodeURIComponent(returnTo));
    return true;
  };

  function broadcast(isMember, source) {
    window.EBMembership.pending = false;
    window.EBMembership.member = !!isMember;
    try {
      document.dispatchEvent(new CustomEvent('eb:membership', {
        detail: { member: !!isMember, source: source },
      }));
    } catch (e) { /* ancient browsers — sale-event.js falls back to the cache */ }
  }

  // Portal "Sign in" links carry ?shop_return={{ request.path }}, which drops
  // the query string (a vessel's ?design=, a variant) and is wrong wherever the
  // markup was rendered for another URL — the cart drawer is re-rendered from
  // /cart?section_id=cart-drawer, so its links said /cart. Rewrite shop_return
  // to the page actually being viewed at click time; the server-rendered value
  // stays as the no-JS fallback. Lives here because every such link is US-only,
  // like this script.
  var PORTAL_LOGIN = 'https://account.elementbrooklyn.com/login';
  function currentShopReturn() {
    var params = new URLSearchParams(window.location.search);
    params.delete('eb_ss_refresh'); // the portal appends its own on the way back
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }
  function rewriteShopReturn(e) {
    var link = e.target && e.target.closest && e.target.closest('a[href^="' + PORTAL_LOGIN + '"]');
    if (!link) return;
    try {
      var url = new URL(link.href);
      if (url.origin + url.pathname !== PORTAL_LOGIN || !url.searchParams.has('shop_return')) return;
      url.searchParams.set('shop_return', currentShopReturn());
      link.href = url.toString();
    } catch (err) { /* keep the server-rendered href */ }
  }
  // Capture phase, so it runs before any handler that stops propagation;
  // auxclick covers middle-click "open in new tab".
  document.addEventListener('click', rewriteShopReturn, true);
  document.addEventListener('auxclick', rewriteShopReturn, true);

  // The cart-validation Function enforces membership at CHECKOUT only, so a
  // member whose shop session isn't signed in (portal sign-in via the header
  // Account icon, arriving from an ad or email) adds to cart fine and is then
  // refused at checkout, where recoverShopSignIn can't help. So for a known
  // member, the first Check out click of a tab session goes through the shop's
  // own sign-in with return_to=/checkout, which creates the shop session first.
  //
  // Trade-off: one extra redirect hop on a member's first checkout per tab, even
  // when the shop session already exists (we can't see it from Liquid or JS
  // under New Customer Accounts). It's silent while their Shopify login is warm
  // and asks for a code once otherwise. Non-members, later clicks, tabs where
  // recoverShopSignIn already ran, and tabs without sessionStorage (no loop
  // guard) go straight to checkout.
  var CHECKOUT_SIGN_IN_KEY = 'eb_ss_checkout_signin_at';
  var CHECKOUT_PATH_RE = /^(\/[a-z]{2}(-[a-z]{2})?)?\/checkout\/?$/i; // /checkout, /en-gb/checkout
  function claimCheckoutSignIn() {
    if (window.EBMembership.member !== true) return false;
    try {
      if (sessionStorage.getItem(CHECKOUT_SIGN_IN_KEY) || sessionStorage.getItem(SHOP_SIGN_IN_KEY)) return false;
      sessionStorage.setItem(CHECKOUT_SIGN_IN_KEY, String(Date.now()));
    } catch (e) {
      return false;
    }
    return true;
  }
  function signInThenCheckout() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.assign('/customer_authentication/login?return_to=' + encodeURIComponent(root + 'checkout'));
  }
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var target = e.target && e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!target) return;

    if (target.tagName === 'A') {
      var href;
      try { href = new URL(target.href, window.location.href); } catch (err) { return; }
      if (href.origin !== window.location.origin || !CHECKOUT_PATH_RE.test(href.pathname)) return;
      if (!claimCheckoutSignIn()) return;
      e.preventDefault();
      signInThenCheckout();
      return;
    }

    // Check out submits the cart form (drawer: #CartDrawer-Form; /cart: #cart),
    // which also carries not-yet-sent quantity edits and the note. Apply those
    // with /cart/update.js before leaving so the detour doesn't drop them —
    // new FormData(form) fires the drawer's `formdata` handler, which swaps
    // positional updates[] for key-based ones. If that fails, fall back to the
    // normal submit (the claimed flag lets the re-click through).
    var form = target.form;
    if (!form || target.disabled || !claimCheckoutSignIn()) return;
    e.preventDefault();
    var body;
    try {
      body = new URLSearchParams(new FormData(form));
    } catch (err) {
      target.click();
      return;
    }
    fetch((window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: body,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update ' + r.status);
        signInThenCheckout();
      })
      .catch(function () { target.click(); });
  });

  if (cached) setMembership(cached.scent_society);

  // Skip the network when the cache is fresh (fixed 2026-09 — it used to
  // revalidate /api/me on EVERY US page view: a cross-origin connection plus a
  // Rails request per page, even with a 5-minute cache). We still revalidate:
  //   - once per browser session (first page of a visit), so a new visit never
  //     trusts a state cached by an earlier one;
  //   - when the cache has expired (CACHE_TTL_MS);
  //   - when the URL carries ?eb_ss_refresh (for the portal to append on its
  //     post-login / post-signup redirect back here — the portal cannot clear
  //     this origin's localStorage itself).
  // Worst case otherwise is the documented ≤5-minute lag after a membership
  // change mid-visit. Listeners still get `eb:membership` either way.
  var SESSION_KEY = 'eb_scent_society_checked';
  var forceRefresh = /[?&]eb_ss_refresh(=|&|$)/.test(window.location.search);
  var checkedThisSession = false;
  try { checkedThisSession = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { /* no storage */ }

  if (cached && checkedThisSession && !forceRefresh) {
    // Deferred so it matches the async timing of the network path —
    // sale-event.js may subscribe after this script runs.
    setTimeout(function () { broadcast(cached.scent_society, 'cache'); }, 0);
    return;
  }

  fetch(PORTAL_API, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(function (r) {
      // A non-OK answer (portal 5xx, deploy, rate limit) is an outage, not a
      // "no": caching it as non-member locked real members out for 5 minutes.
      // /api/me returns 200 even when logged out, so only 2xx is an answer.
      if (!r.ok) throw new Error('portal /api/me ' + r.status);
      return r.json();
    })
    .then(function (data) {
      var isMember = !!(data && data.logged_in && data.scent_society);
      setMembership(isMember);
      writeCache(isMember);
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* ignore */ }
      broadcast(isMember, 'portal');
    })
    .catch(function () {
      // Portal unreachable or erroring — leave the current state alone and
      // don't mark the session checked (the next page retries), but still tell
      // listeners so they stop waiting (cached value or non-member).
      broadcast(cached ? cached.scent_society : false, 'cache');
    });
})();
